import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import axios from 'axios';
import MessageContent from './MessageContent';
import ConfirmationModal from './ConfirmationModal';
import ReactionTooltip from './ReactionTooltip';
import SwipeableMessage from './SwipeableMessage';
import Avatar from './Avatar';
import './Chat.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

const EMOJI_OPTIONS = ['👍', '❤️', '😂', '😮', '😢', '😡', '🔥', '✅'];

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [chatSettings, setChatSettings] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState(0);
  const [darkMode, setDarkMode] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    messageId: null,
    title: '',
    message: ''
  });
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [mobileMessageModal, setMobileMessageModal] = useState({
    isOpen: false,
    message: null,
    position: { x: 0, y: 0 }
  });
  const [editingMessage, setEditingMessage] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [actionFeedback, setActionFeedback] = useState({
    show: false,
    message: '',
    type: 'success' // 'success', 'error', 'info'
  });
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinnedBar, setShowPinnedBar] = useState(true);
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [showRefreshWarning, setShowRefreshWarning] = useState(false);
  
  const { user, token, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Show refresh warning on component mount
  useEffect(() => {
    // Show warning for 10 seconds when chat loads
    setShowRefreshWarning(true);
    const timer = setTimeout(() => {
      setShowRefreshWarning(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  // Detect if device is mobile
  const isMobile = () => {
    return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Check if user is currently muted
  const isUserMuted = () => {
    if (!user?.isMuted) return false;
    
    // If there's no mutedUntil date, it's a permanent mute
    if (!user.mutedUntil) return true;
    
    // Check if the mute period has expired
    const now = new Date();
    const muteExpiry = new Date(user.mutedUntil);
    return now < muteExpiry;
  };

  // Check for mute expiry periodically
  useEffect(() => {
    if (!user?.isMuted || !user?.mutedUntil) return;

    const checkMuteExpiry = () => {
      const now = new Date();
      const muteExpiry = new Date(user.mutedUntil);
      
      if (now >= muteExpiry) {
        // Mute period has expired, update user status
        updateUser({ 
          isMuted: false, 
          mutedUntil: null 
        });
        setErrorMessage('Your mute period has expired. You can now send messages.');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    };

    // Check immediately
    checkMuteExpiry();

    // Check every 30 seconds
    const interval = setInterval(checkMuteExpiry, 30000);

    return () => clearInterval(interval);
  }, [user?.isMuted, user?.mutedUntil, updateUser]);

  // Load pinned messages
  const loadPinnedMessages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/chat/pinned`);
      setPinnedMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load pinned messages:', error);
    }
  };

  // Handle search
  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      // For now, search locally in messages
      const results = messages.filter(msg => 
        msg.content.toLowerCase().includes(query.toLowerCase()) ||
        msg.user.displayName.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 20); // Limit to 20 results
      
      setSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
      showActionFeedback('Search failed', 'error');
    }
  };

  // Jump to pinned message
  const jumpToPinnedMessage = (messageId) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messageElement.classList.add('highlight-pinned');
      setTimeout(() => messageElement.classList.remove('highlight-pinned'), 2000);
    }
    setShowPinnedPanel(false);
  };

  // Get pinned message preview text
  const getPinnedPreviewText = (message) => {
    if (!message.content) return '📎 Media message';
    if (message.content.length > 60) {
      return message.content.substring(0, 60) + '...';
    }
    return message.content;
  };

  // Show action feedback
  const showActionFeedback = (message, type = 'success') => {
    setActionFeedback({
      show: true,
      message,
      type
    });
    setTimeout(() => {
      setActionFeedback({
        show: false,
        message: '',
        type: 'success'
      });
    }, 3000);
  };

  // Handle message tap (both mobile and desktop)
  const handleMessageTap = (message, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    setMobileMessageModal({
      isOpen: true,
      message: message,
      position: { x: 0, y: 0 } // We'll position it with CSS instead
    });
  };

  // Handle copy message
  const handleCopyMessage = async (message) => {
    try {
      await navigator.clipboard.writeText(message.content);
      showActionFeedback('Message copied to clipboard!', 'success');
      closeMobileMessageModal();
    } catch (error) {
      console.error('Failed to copy message:', error);
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = message.content;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showActionFeedback('Message copied to clipboard!', 'success');
        closeMobileMessageModal();
      } catch (fallbackError) {
        showActionFeedback('Failed to copy message', 'error');
        console.error('Fallback copy failed:', fallbackError);
      }
    }
  };

  // Close mobile message modal
  const closeMobileMessageModal = () => {
    setMobileMessageModal({
      isOpen: false,
      message: null,
      position: { x: 0, y: 0 }
    });
  };

  // Handle pin/unpin message
  const handlePinMessage = async (message) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/chat/messages/${message._id}/pin`);
      
      // Update the message in local state
      setMessages(prev => prev.map(msg => 
        msg._id === message._id 
          ? { ...msg, isPinned: !msg.isPinned, pinnedBy: !msg.isPinned ? user : null, pinnedAt: !msg.isPinned ? new Date() : null }
          : msg
      ));
      
      // Reload pinned messages
      await loadPinnedMessages();
      
      const action = message.isPinned ? 'unpinned' : 'pinned';
      showActionFeedback(`Message ${action} successfully!`, 'success');
      closeMobileMessageModal();
    } catch (error) {
      console.error('Failed to pin/unpin message:', error);
      const action = message.isPinned ? 'unpin' : 'pin';
      showActionFeedback(`Failed to ${action} message`, 'error');
    }
  };

  // Check if message can be edited
  const canEditMessage = (message) => {
    // Only message author can edit
    if (message.user._id !== user.id) return false;
    
    // Check if message is within edit time limit (15 minutes)
    const messageTime = new Date(message.createdAt);
    const now = new Date();
    const timeDiff = (now - messageTime) / (1000 * 60); // minutes
    
    return timeDiff <= 15; // 15 minutes edit window
  };

  // Handle edit message
  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setEditContent(message.content);
    closeMobileMessageModal();
  };

  // Save edited message
  const handleSaveEdit = async () => {
    if (!editContent.trim() || !editingMessage) return;

    try {
      const response = await axios.put(`${API_BASE_URL}/chat/messages/${editingMessage._id}`, {
        content: editContent.trim()
      });

      // Update the message in local state
      setMessages(prev => prev.map(msg => 
        msg._id === editingMessage._id 
          ? { ...msg, content: editContent.trim(), isEdited: true, editedAt: new Date() }
          : msg
      ));

      showActionFeedback('Message edited successfully!', 'success');
      setEditingMessage(null);
      setEditContent('');
    } catch (error) {
      console.error('Failed to edit message:', error);
      showActionFeedback('Failed to edit message', 'error');
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditContent('');
  };

  // Initialize socket connection
  useEffect(() => {
    if (!token) return;

    const newSocket = io(SOCKET_URL, {
      auth: { token }
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      console.log('Connected to server');
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
      console.log('Disconnected from server');
    });

    newSocket.on('new-message', (message) => {
      setMessages(prev => [...prev, message]);
      scrollToBottom();
    });

    newSocket.on('reaction-updated', (data) => {
      setMessages(prev => prev.map(msg => 
        msg._id === data.messageId 
          ? { ...msg, reactions: data.reactions }
          : msg
      ));
    });

    newSocket.on('user-joined', (data) => {
      console.log(`${data.username} joined the chat`);
    });

    newSocket.on('user-left', (data) => {
      console.log(`${data.username} left the chat`);
    });

    newSocket.on('user-typing', (username) => {
      setTypingUsers(prev => {
        if (!prev.includes(username)) {
          return [...prev, username];
        }
        return prev;
      });
    });

    newSocket.on('user-stop-typing', (username) => {
      setTypingUsers(prev => prev.filter(u => u !== username));
    });

    newSocket.on('error', (error) => {
      console.error('Socket error:', error);
      setErrorMessage(error.message);
      setTimeout(() => setErrorMessage(''), 5000);
    });

    // Listen for mute/unmute events
    newSocket.on('user-muted', (data) => {
      console.log('User muted:', data);
      // If the muted user is the current user, update their state
      if (data.userId === user.id) {
        // Update user context with mute status
        updateUser({ 
          isMuted: true, 
          mutedUntil: data.mutedUntil 
        });
        setErrorMessage(`You have been muted by ${data.mutedBy}`);
        setTimeout(() => setErrorMessage(''), 5000);
      }
    });

    newSocket.on('user-unmuted', (data) => {
      console.log('User unmuted:', data);
      // If the unmuted user is the current user, update their state
      if (data.userId === user.id) {
        // Update user context with unmute status
        updateUser({ 
          isMuted: false, 
          mutedUntil: null 
        });
        setErrorMessage(`You have been unmuted by ${data.unmutedBy}`);
        setTimeout(() => setErrorMessage(''), 3000);
      }
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [token, user?.id, updateUser]);

  // Load initial messages and chat settings
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading chat data...');
        
        // Load messages first
        const messagesResponse = await axios.get(`${API_BASE_URL}/chat/messages`);
        console.log('Messages loaded:', messagesResponse.data.messages.length);
        setMessages(messagesResponse.data.messages);
        
        // Load settings (optional)
        try {
          const settingsResponse = await axios.get(`${API_BASE_URL}/admin/chat-settings`);
          console.log('Settings loaded:', settingsResponse.data.settings);
          setChatSettings(settingsResponse.data.settings);
        } catch (settingsError) {
          console.log('Settings not available (not admin)');
          setChatSettings(null);
        }
        
        // Load users for mentions
        try {
          const usersResponse = await axios.get(`${API_BASE_URL}/chat/users-for-mentions`);
          console.log('Users loaded for mentions:', usersResponse.data.users.length);
          setUsers(usersResponse.data.users || []);
        } catch (usersError) {
          console.error('Failed to load users for mentions:', usersError);
          console.error('Users error details:', usersError.response?.data);
          setUsers([]);
        }
        
        // Load pinned messages
        await loadPinnedMessages();
        
        setTimeout(scrollToBottom, 100);
        
        // Show swipe hint for new users (only once)
        const hasSeenSwipeHint = localStorage.getItem('hasSeenSwipeHint');
        if (!hasSeenSwipeHint && messagesResponse.data.messages.length > 0) {
          setTimeout(() => {
            setShowSwipeHint(true);
            setTimeout(() => {
              setShowSwipeHint(false);
              localStorage.setItem('hasSeenSwipeHint', 'true');
            }, 3000);
          }, 2000);
        }
      } catch (error) {
        console.error('Failed to load main data:', error);
      }
    };

    loadData();
  }, []);

  // Handle click outside to close mobile modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMessageModal.isOpen && !event.target.closest('.mobile-message-modal') && !event.target.closest('.message-content')) {
        closeMobileMessageModal();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && mobileMessageModal.isOpen) {
        closeMobileMessageModal();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMessageModal.isOpen]);

  // Handle scroll to show/hide scroll button
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside to close mentions dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMentions && !event.target.closest('.mentions-dropdown') && !event.target.closest('.message-input')) {
        setShowMentions(false);
        setMentionQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMentions]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !socket) return;

    socket.emit('send-message', {
      content: newMessage.trim(),
      replyTo: replyingTo?._id
    });

    setNewMessage('');
    setReplyingTo(null);
    handleStopTyping();
    
    // Reset textarea height
    setTimeout(() => {
      const textarea = document.querySelector('.message-input');
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = '20px';
      }
    }, 0);
  };

  const handleTyping = () => {
    if (!socket) return;

    socket.emit('typing');

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      handleStopTyping();
    }, 2000);
  };

  const handleStopTyping = () => {
    if (!socket) return;

    socket.emit('stop-typing');
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const shouldShowDateSeparator = (currentMessage, previousMessage) => {
    if (!previousMessage) return true;
    
    const currentDate = new Date(currentMessage.createdAt).toDateString();
    const previousDate = new Date(previousMessage.createdAt).toDateString();
    
    return currentDate !== previousDate;
  };

  const handleReply = (message) => {
    setReplyingTo(message);
  };

  const handleReaction = (messageId, emoji) => {
    if (!socket) return;
    socket.emit('toggle-reaction', { messageId, emoji });
    setShowEmojiPicker(null);
  };

  const scrollToMessage = (messageId) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messageElement.classList.add('highlight');
      setTimeout(() => messageElement.classList.remove('highlight'), 2000);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    setConfirmModal({
      isOpen: true,
      messageId: messageId,
      title: 'Delete Message',
      message: 'Are you sure you want to delete this message? This action cannot be undone.'
    });
    closeMobileMessageModal(); // Close the modal when showing confirmation
  };

  const confirmDeleteMessage = async () => {
    const messageId = confirmModal.messageId;
    
    try {
      await axios.delete(`${API_BASE_URL}/chat/messages/${messageId}`);
      // Remove message from local state
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
      setConfirmModal({ isOpen: false, messageId: null, title: '', message: '' });
      showActionFeedback('Message deleted successfully!', 'success');
    } catch (error) {
      console.error('Failed to delete message:', error);
      showActionFeedback('Failed to delete message', 'error');
      setConfirmModal({ isOpen: false, messageId: null, title: '', message: '' });
    }
  };

  const cancelDeleteMessage = () => {
    setConfirmModal({ isOpen: false, messageId: null, title: '', message: '' });
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    setNewMessage(value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 400) + 'px';
    
    // Check for @mentions
    const textBeforeCursor = value.slice(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      console.log('Mention detected:', mentionMatch[1], 'Users available:', users.length);
      setMentionQuery(mentionMatch[1]);
      setMentionPosition(cursorPosition);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery('');
    }
    
    handleTyping();
  };

  const handleMentionSelect = (selectedUser) => {
    const username = selectedUser.username || selectedUser.displayName.replace(/\s+/g, '');
    const beforeMention = newMessage.slice(0, mentionPosition - mentionQuery.length - 1);
    const afterMention = newMessage.slice(mentionPosition);
    const newValue = beforeMention + `@${username} ` + afterMention;
    
    setNewMessage(newValue);
    setShowMentions(false);
    setMentionQuery('');
    
    // Focus back on input and set cursor position
    setTimeout(() => {
      const input = document.querySelector('.message-input');
      if (input) {
        const newCursorPos = beforeMention.length + username.length + 2;
        input.focus();
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const getFilteredUsers = () => {
    console.log('Getting filtered users, total users:', users.length, 'query:', mentionQuery);
    
    if (!mentionQuery) {
      const result = users.slice(0, 5);
      console.log('No query, returning first 5 users:', result);
      return result;
    }
    
    const filtered = users.filter(u => {
      const displayNameMatch = u.displayName.toLowerCase().includes(mentionQuery.toLowerCase());
      const usernameMatch = u.username && u.username.toLowerCase().includes(mentionQuery.toLowerCase());
      return displayNameMatch || usernameMatch;
    }).slice(0, 5);
    
    console.log('Filtered users:', filtered);
    return filtered;
  };

  // Group messages by sender and time
  const groupMessages = (messages) => {
    const groups = [];
    let currentGroup = null;
    
    messages.forEach((message, index) => {
      const prevMessage = messages[index - 1];
      const shouldGroup = prevMessage && 
        prevMessage.user._id === message.user._id &&
        new Date(message.createdAt) - new Date(prevMessage.createdAt) < 300000; // 5 minutes
      
      if (!shouldGroup) {
        currentGroup = {
          user: message.user,
          messages: [message],
          timestamp: message.createdAt,
          isNewSender: !prevMessage || prevMessage.user._id !== message.user._id
        };
        groups.push(currentGroup);
      } else {
        currentGroup.messages.push(message);
      }
    });
    
    return groups;
  };

  const messageGroups = groupMessages(messages);

  return (
    <div className={`chat-container ${darkMode ? 'dark' : ''}`} data-theme={darkMode ? 'dark' : 'light'}>
      {/* Main Chat Area */}
      <div className="chat-main">
        {/* Chat Header */}
        <div className="chat-header">
          <div className="header-left">
            <div className="group-avatar">
              C
            </div>
            <div className="header-info">
              <h1 className="header-title">Class Group Chat</h1>
              <p className="header-subtitle">
                <span className="online-indicator"></span>
                {users.length} members • {isConnected ? 'Connected' : 'Connecting...'}
              </p>
            </div>
          </div>
          <div className="header-right">
            {/* Desktop Navigation */}
            <div className="desktop-nav">
              <button 
                className="header-action-btn"
                onClick={() => setShowSearchModal(true)}
                title="Search Messages"
              >
                <svg className="action-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
                <span className="action-text">Search</span>
              </button>
              <button 
                className={`header-action-btn ${pinnedMessages.length > 0 ? 'has-pinned' : ''}`}
                onClick={() => {
                  if (pinnedMessages.length === 1) {
                    jumpToPinnedMessage(pinnedMessages[0]._id);
                  } else if (pinnedMessages.length > 1) {
                    setShowPinnedPanel(true);
                  }
                }}
                disabled={pinnedMessages.length === 0}
                title={pinnedMessages.length > 0 ? `${pinnedMessages.length} pinned messages` : 'No pinned messages'}
              >
                <svg className="action-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                </svg>
                <span className="action-text">Pinned {pinnedMessages.length > 0 && `(${pinnedMessages.length})`}</span>
                {pinnedMessages.length > 0 && (
                  <span className="pinned-badge">{pinnedMessages.length}</span>
                )}
              </button>
              <button 
                className="header-action-btn"
                onClick={() => navigate('/profile')}
                title="Profile"
              >
                <svg className="action-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span className="action-text">Profile</span>
              </button>
              {user?.role === 'admin' && (
                <button 
                  className="header-action-btn"
                  onClick={() => navigate('/admin')}
                  title="Admin Panel"
                >
                  <svg className="action-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                  <span className="action-text">Admin</span>
                </button>
              )}
              <button 
                className="header-action-btn danger"
                onClick={logout}
                title="Logout"
              >
                <svg className="action-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                </svg>
                <span className="action-text">Logout</span>
              </button>
            </div>

            {/* Mobile Navigation */}
            <div className="mobile-nav">
              <button 
                className="mobile-nav-btn"
                onClick={() => setShowSearchModal(true)}
                title="Search"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </button>
              {pinnedMessages.length > 0 && (
                <button 
                  className="mobile-nav-btn"
                  onClick={() => {
                    if (pinnedMessages.length === 1) {
                      jumpToPinnedMessage(pinnedMessages[0]._id);
                    } else if (pinnedMessages.length > 1) {
                      setShowPinnedPanel(true);
                    }
                  }}
                  title="Pinned Messages"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                  </svg>
                  {pinnedMessages.length > 1 && (
                    <span className="mobile-badge">{pinnedMessages.length}</span>
                  )}
                </button>
              )}
              <button 
                className="mobile-nav-btn"
                onClick={() => navigate('/profile')}
                title="Profile"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </button>
              {user?.role === 'admin' && (
                <button 
                  className="mobile-nav-btn admin"
                  onClick={() => navigate('/admin')}
                  title="Admin"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
              <button 
                className="mobile-nav-btn danger"
                onClick={logout}
                title="Logout"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Pinned Message Bar */}
        {pinnedMessages.length > 0 && showPinnedBar && (
          <div 
            className="pinned-message-bar"
            onClick={() => {
              if (pinnedMessages.length === 1) {
                jumpToPinnedMessage(pinnedMessages[0]._id);
              } else {
                setShowPinnedPanel(true);
              }
            }}
          >
            <div className="pinned-bar-content">
              <div className="pinned-bar-left">
                <span className="pinned-label">
                  📌 {pinnedMessages.length === 1 ? '1 pinned message' : `${pinnedMessages.length} pinned messages`}
                  {pinnedMessages.length > 0 && (
                    <span className="pinned-preview">
                      {' · '}
                      <span className="pinned-author">@{pinnedMessages[0].user.displayName}</span>
                      {' '}
                      {getPinnedPreviewText(pinnedMessages[0])}
                    </span>
                  )}
                </span>
              </div>
              
              <button 
                className="pinned-close-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPinnedBar(false);
                }}
                title="Hide pinned bar"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Error Banner */}
        {errorMessage && (
          <div className="error-banner">
            <svg className="banner-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {errorMessage}
          </div>
        )}

        {/* Refresh Warning Banner */}
        {showRefreshWarning && (
          <div className="refresh-warning-banner">
            <svg className="banner-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            🔄 Security Notice: Refreshing the page will automatically log you out for security
            <button 
              className="dismiss-warning-btn"
              onClick={() => setShowRefreshWarning(false)}
              title="Dismiss warning"
            >
              ×
            </button>
          </div>
        )}

        {/* Announcement Banner */}
        {chatSettings?.announcementMode && (
          <div className="announcement-banner">
            <svg className="banner-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clipRule="evenodd" />
            </svg>
            Announcement Mode is active. Only admins and moderators can send messages.
          </div>
        )}

        {/* Messages Container */}
        <div className="messages-container" ref={messagesContainerRef}>
          {messageGroups.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">💬</div>
              <div className="empty-state-text">Start the conversation</div>
              <div className="empty-state-subtext">Be the first to send a message to the class!</div>
            </div>
          ) : (
            messageGroups.map((group, groupIndex) => (
              <React.Fragment key={`group-${groupIndex}`}>
                {/* Date Separator */}
                {(groupIndex === 0 || shouldShowDateSeparator(group.messages[0], messageGroups[groupIndex - 1]?.messages[0])) && (
                  <div className="date-separator">
                    <span>{formatDate(group.timestamp)}</span>
                  </div>
                )}
                
                {/* Message Group */}
                <div className={`message-group ${!group.isNewSender ? 'same-user' : ''}`}>
                  {/* Group Header */}
                  <div className="message-group-header">
                    <div className="message-group-avatar">
                      <Avatar
                        user={group.user}
                        alt={group.user.displayName}
                        size={40}
                      />
                    </div>
                    <span className="message-group-name">
                      {group.user.displayName}
                      {group.user.role !== 'member' && (
                        <span className={`member-role-badge ${group.user.role}`}>
                          {group.user.role}
                        </span>
                      )}
                    </span>
                    <span className="message-group-time">
                      {formatTime(group.timestamp)}
                    </span>
                  </div>
                  
                  {/* Messages in Group */}
                  {group.messages.map((message) => (
                    <SwipeableMessage
                      key={message._id}
                      onReply={() => handleReply(message)}
                    >
                      <div 
                        id={`message-${message._id}`}
                        className="message"
                      >
                        {/* Reply Preview */}
                        {message.replyTo && (
                          <div 
                            className="reply-preview"
                            onClick={() => scrollToMessage(message.replyTo._id)}
                          >
                            <div className="reply-content">
                              <span className="reply-author">{message.replyTo.user?.displayName}</span>
                              <span className="reply-text">{message.replyTo.content}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Message Content */}
                        <div 
                          className="message-content"
                          onClick={(e) => {
                            handleMessageTap(message, e);
                          }}
                          onTouchStart={(e) => {
                            if (isMobile()) {
                              e.preventDefault();
                            }
                          }}
                        >
                          <MessageContent content={message.content} />
                          <div className="message-meta">
                            {message.isEdited && <span className="edited-label">edited</span>}
                            {message.isEdited && <span className="meta-separator">·</span>}
                            <span className="message-timestamp">{formatTime(message.createdAt)}</span>
                          </div>
                        </div>

                        {/* Message Reactions */}
                        {message.reactions && message.reactions.length > 0 && (
                          <div className="message-reactions">
                            {message.reactions.map((reaction, idx) => {
                              const userReacted = reaction.users.some(u => u._id === user.id || u === user.id);
                              
                              return (
                                <ReactionTooltip 
                                  key={idx} 
                                  reaction={reaction} 
                                  currentUserId={user.id}
                                >
                                  <button
                                    className={`reaction ${userReacted ? 'own-reaction' : ''}`}
                                    onClick={() => handleReaction(message._id, reaction.emoji)}
                                  >
                                    {reaction.emoji} {reaction.users.length}
                                  </button>
                                </ReactionTooltip>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </SwipeableMessage>
                  ))}
                </div>
              </React.Fragment>
            ))
          )}
          
          {/* Typing Indicator */}
          {typingUsers.length > 0 && (
            <div className="typing-indicator">
              <span>
                {typingUsers.length === 1 
                  ? `${typingUsers[0]} is typing...`
                  : `${typingUsers.join(', ')} are typing...`
                }
              </span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to Bottom Button */}
        {showScrollButton && (
          <button 
            className="scroll-to-bottom"
            onClick={scrollToBottom}
          >
          </button>
        )}

        {/* Message Composer */}
        {isUserMuted() ? (
          <div className="mute-notice">
            <div className="mute-icon">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM15.657 6.343a1 1 0 011.414 1.414L15.414 9.414l1.657 1.657a1 1 0 11-1.414 1.414L14 10.828l-1.657 1.657a1 1 0 11-1.414-1.414L12.586 9.414l-1.657-1.657a1 1 0 111.414-1.414L14 7.686l1.657-1.343z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="mute-content">
              <h3>You are muted by admin</h3>
              <p>
                {user?.mutedUntil 
                  ? `You are temporarily muted until ${new Date(user.mutedUntil).toLocaleString()}`
                  : 'You have been permanently muted and cannot send messages.'
                }
              </p>
            </div>
          </div>
        ) : chatSettings?.announcementMode && !['admin', 'moderator'].includes(user.role) ? (
          <div className="announcement-mode-notice">
            <div className="announcement-icon">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="announcement-content">
              <h3>Only admins can message now</h3>
              <p>Announcement mode is active. Regular messaging is temporarily disabled.</p>
            </div>
          </div>
        ) : (
          <form className="message-form" onSubmit={handleSendMessage}>
          {/* Reply Bar */}
          {replyingTo && (
            <div className="reply-bar">
              <div className="reply-info">
                <span>Replying to {replyingTo.user.displayName}</span>
                <span className="reply-preview-text">{replyingTo.content}</span>
              </div>
              <button 
                type="button"
                className="cancel-reply"
                onClick={() => setReplyingTo(null)}
              >
                ×
              </button>
            </div>
          )}
          
          {/* Input Container */}
          <div className="input-container">
            {/* Mentions Dropdown */}
            {showMentions && getFilteredUsers().length > 0 && (
              <div className="mentions-dropdown">
                {getFilteredUsers().map(mentionUser => (
                  <button
                    key={mentionUser._id}
                    type="button"
                    className="mention-option"
                    onClick={() => handleMentionSelect(mentionUser)}
                  >
                    <Avatar
                      user={mentionUser}
                      alt={mentionUser.displayName}
                      size={24}
                      className="mention-avatar"
                    />
                    <div className="mention-info">
                      <span className="mention-name">{mentionUser.displayName}</span>
                      {mentionUser.username && (
                        <span className="mention-username">@{mentionUser.username}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {/* Input Row */}
            <div className="input-row">
              <textarea
                value={newMessage}
                onChange={handleInputChange}
                placeholder={replyingTo ? "Reply..." : "Type a message..."}
                className="message-input"
                disabled={!isConnected}
                rows={1}
                onKeyDown={(e) => {
                  if (showMentions && getFilteredUsers().length > 0) {
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                      e.preventDefault();
                      return;
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const firstUser = getFilteredUsers()[0];
                      if (firstUser) {
                        handleMentionSelect(firstUser);
                      }
                      return;
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setShowMentions(false);
                      setMentionQuery('');
                      return;
                    }
                  }
                  
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />
              <button 
                type="submit" 
                className="send-button"
                disabled={!newMessage.trim() || !isConnected}
              >
              </button>
            </div>
          </div>
        </form>
        )}

        {/* Edit Message Interface */}
        {editingMessage && (
          <div className="edit-message-overlay">
            <div className="edit-message-container">
              <div className="edit-message-header">
                <h3>Edit Message</h3>
                <button className="edit-close-btn" onClick={handleCancelEdit}>×</button>
              </div>
              <div className="edit-message-content">
                <div className="edit-original-message">
                  <span className="edit-label">Original:</span>
                  <div className="edit-original-text">{editingMessage.content}</div>
                </div>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="edit-message-input"
                  placeholder="Edit your message..."
                  maxLength={2000}
                  rows={3}
                />
                <div className="edit-message-actions">
                  <button 
                    className="edit-cancel-btn" 
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                  <button 
                    className="edit-save-btn" 
                    onClick={handleSaveEdit}
                    disabled={!editContent.trim() || editContent.trim() === editingMessage.content}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Feedback */}
      {actionFeedback.show && (
        <div className={`action-feedback ${actionFeedback.type}`}>
          <div className="feedback-content">
            {actionFeedback.type === 'success' && (
              <svg className="feedback-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
            {actionFeedback.type === 'error' && (
              <svg className="feedback-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            {actionFeedback.type === 'info' && (
              <svg className="feedback-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            )}
            <span>{actionFeedback.message}</span>
          </div>
        </div>
      )}

      {/* Search Modal */}
      {showSearchModal && (
        <>
          <div className="search-modal-overlay" onClick={() => setShowSearchModal(false)}></div>
          <div className="search-modal">
            <div className="search-modal-header">
              <h3>Search Messages</h3>
              <button className="search-close-btn" onClick={() => setShowSearchModal(false)}>×</button>
            </div>
            <div className="search-modal-content">
              <div className="search-input-container">
                <svg className="search-input-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    handleSearch(e.target.value);
                  }}
                  className="search-input"
                  autoFocus
                />
              </div>
              
              <div className="search-results">
                {searchQuery && searchResults.length === 0 && (
                  <div className="search-no-results">
                    No messages found for "{searchQuery}"
                  </div>
                )}
                
                {searchResults.map(message => (
                  <div 
                    key={message._id}
                    className="search-result-item"
                    onClick={() => {
                      scrollToMessage(message._id);
                      setShowSearchModal(false);
                    }}
                  >
                    <div className="search-result-header">
                      <Avatar
                        user={message.user}
                        alt={message.user.displayName}
                        size={24}
                        className="search-result-avatar"
                      />
                      <span className="search-result-name">{message.user.displayName}</span>
                      <span className="search-result-time">{formatTime(message.createdAt)}</span>
                    </div>
                    <div className="search-result-content">
                      <MessageContent content={message.content} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Pinned Messages Panel */}
      {showPinnedPanel && (
        <>
          <div className="pinned-panel-overlay" onClick={() => setShowPinnedPanel(false)}></div>
          <div className="pinned-messages-panel">
            <div className="pinned-panel-header">
              <h3>Pinned Messages</h3>
              <button className="pinned-panel-close-btn" onClick={() => setShowPinnedPanel(false)}>×</button>
            </div>
            <div className="pinned-panel-content">
              {pinnedMessages.map(message => (
                <div 
                  key={message._id}
                  className="pinned-panel-item"
                  onClick={() => jumpToPinnedMessage(message._id)}
                >
                  <div className="pinned-item-header">
                    <Avatar
                      user={message.user}
                      alt={message.user.displayName}
                      size={28}
                      className="pinned-item-avatar"
                    />
                    <div className="pinned-item-info">
                      <span className="pinned-item-name">{message.user.displayName}</span>
                      <span className="pinned-item-time">{formatTime(message.createdAt)}</span>
                    </div>
                    <svg className="pinned-item-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="pinned-item-content">
                    <MessageContent content={message.content} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={cancelDeleteMessage}
        onConfirm={confirmDeleteMessage}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Swipe Hint */}
      {showSwipeHint && (
        <div className="swipe-reply-hint">
          💡 Swipe messages right to reply quickly!
        </div>
      )}

      {/* Mobile Message Modal */}
      {mobileMessageModal.isOpen && mobileMessageModal.message && (
        <>
          <div className="mobile-modal-overlay" onClick={closeMobileMessageModal}></div>
          <div className="mobile-message-modal">
            <div className="mobile-modal-header">
              <div className="modal-message-info">
                <Avatar
                  user={mobileMessageModal.message.user}
                  alt={mobileMessageModal.message.user.displayName}
                  size={32}
                  className="modal-avatar"
                />
                <div className="modal-user-info">
                  <span className="modal-username">{mobileMessageModal.message.user.displayName}</span>
                  <span className="modal-timestamp">{formatTime(mobileMessageModal.message.createdAt)}</span>
                </div>
              </div>
              <button className="modal-close-btn" onClick={closeMobileMessageModal}>×</button>
            </div>

            <div className="mobile-modal-content">
              <div className="modal-message-preview">
                <MessageContent content={mobileMessageModal.message.content} />
              </div>

              {/* Action Buttons */}
              <div className="modal-actions">
                <button 
                  className="modal-action-btn reply"
                  onClick={() => {
                    handleReply(mobileMessageModal.message);
                    closeMobileMessageModal();
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                  </svg>
                  Reply
                </button>

                <button 
                  className="modal-action-btn copy"
                  onClick={() => handleCopyMessage(mobileMessageModal.message)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copy
                </button>

                <button 
                  className="modal-action-btn pin"
                  onClick={() => handlePinMessage(mobileMessageModal.message)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    {mobileMessageModal.message.isPinned ? 'Unpin' : 'Pin'}
                  </button>

                {canEditMessage(mobileMessageModal.message) && (
                  <button 
                    className="modal-action-btn edit"
                    onClick={() => handleEditMessage(mobileMessageModal.message)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Edit
                  </button>
                )}

                {(mobileMessageModal.message.user._id === user.id || ['admin', 'moderator'].includes(user.role)) && (
                  <button 
                    className="modal-action-btn delete"
                    onClick={() => {
                      handleDeleteMessage(mobileMessageModal.message._id);
                      closeMobileMessageModal();
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3,6 5,6 21,6"/>
                      <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                    </svg>
                    Delete
                  </button>
                )}
              </div>

              {/* Emoji Reactions */}
              <div className="modal-emoji-section">
                <h4>React with:</h4>
                <div className="modal-emoji-grid">
                  {EMOJI_OPTIONS.map(emoji => (
                    <button
                      key={emoji}
                      className="modal-emoji-btn"
                      onClick={() => {
                        handleReaction(mobileMessageModal.message._id, emoji);
                        closeMobileMessageModal();
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reaction List */}
              {mobileMessageModal.message.reactions && mobileMessageModal.message.reactions.length > 0 && (
                <div className="modal-reactions-section">
                  <h4>
                    Reactions:
                    {mobileMessageModal.message.reactions.reduce((total, reaction) => total + reaction.users.length, 0) > 4 && (
                      <span className="reactions-scroll-hint">scroll to see all</span>
                    )}
                  </h4>
                  <div className="modal-reactions-list">
                    {mobileMessageModal.message.reactions.map((reaction, idx) => (
                      <div key={idx} className="modal-reaction-item">
                        <span className="reaction-emoji">{reaction.emoji}</span>
                        <div className="reaction-users">
                          {reaction.users.map((reactUser, userIdx) => (
                            <div key={userIdx} className="reaction-user">
                              <Avatar
                                user={reactUser}
                                alt={reactUser.displayName || reactUser.username}
                                size={18}
                                className="reaction-user-avatar"
                              />
                              <span className="reaction-user-name">
                                {reactUser.displayName || reactUser.username}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Chat;